/**
 * Warm the browser HTTP cache for an image URL and wait until the pixels are
 * decoded. Used by the render-completed WS handler to hold the progress
 * overlay until the finished render can paint instantly — mirrors Apple
 * (Kingfisher `ImagePrefetcher`) and Android (Coil `enqueue().job.await()`).
 *
 * Resolves `true` when the image is ready, `false` on failure or timeout.
 * Callers must treat `false` as "stop holding the UI", never as an error —
 * fail-open so a CDN hiccup can't pin a progress overlay forever.
 */

interface PrefetchImageOptions {
  /** Total load attempts. Retries cover the CDN propagation race where the
   *  completed event can arrive moments before the file is readable. */
  attempts?: number;
  retryDelayMs?: number;
  /** Overall deadline across all attempts. */
  timeoutMs?: number;
}

export async function prefetchImage(
  url: string,
  { attempts = 3, retryDelayMs = 1500, timeoutMs = 12000 }: PrefetchImageOptions = {}
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return false;

    if (await loadAndDecodeOnce(url, remaining)) return true;

    if (attempt < attempts - 1) {
      const pause = Math.min(retryDelayMs, deadline - Date.now());
      if (pause <= 0) return false;
      await new Promise((resolve) => setTimeout(resolve, pause));
    }
  }
  return false;
}

function loadAndDecodeOnce(url: string, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new window.Image();
    let settled = false;
    const settle = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve(ok);
    };
    const timer = setTimeout(() => settle(false), timeoutMs);

    img.onload = () => {
      // decode() guarantees rasterized pixels, not just downloaded bytes, so
      // the first on-screen paint of the render can't lag the overlay fade.
      img
        .decode()
        .then(() => settle(true))
        .catch(() => settle(true)); // decode failure still means bytes are cached
    };
    img.onerror = () => settle(false);
    img.src = url;
  });
}
